import { GridRenderEditCellParams } from '@mui/x-data-grid-premium'
import React from 'react'
import SingleDropdownCell from 'src/components/DataGrid/cells/SingleDropdownCell/SingleDropdownCell'
import TextFieldCell from 'src/components/DataGrid/cells/TextFieldCell/TextFieldCell'
import { useExpenseCategoryDropdownOptions } from 'src/components/ExpenseCategoryDropdownV2/useExpenseCategoryDropdownOptions'
import { TransactionsDataGridRowModel } from 'src/components/TransactionsDataGrid/transactionsDataGrid.types'
import { useExtendLocation } from 'src/hooks/locationHooks'
import { useTranslate } from 'src/localization/Trns'
import { ExpenseCategoryResponse } from 'src/queries/organizations/expenseCategories.queries'

interface TransactionExpenseCategoryDropdownCellProps {
  params: GridRenderEditCellParams<TransactionsDataGridRowModel, string>
  editMode: 'row' | 'cell'
  category: ExpenseCategoryResponse
  loading: boolean
  disabled?: boolean
}

const TransactionExpenseCategoryDropdownCell: React.FC<
  TransactionExpenseCategoryDropdownCellProps
> = (props): React.ReactElement => {
  const translate = useTranslate()
  const transactionId = props.params.row.transaction.id
  const extendLocation = useExtendLocation()

  const { optionGroups, infiniteScrollProps } =
    useExpenseCategoryDropdownOptions({
      expenseCategoryId: props.category.id,
      transactionId,
      freeTextAllowed: props.category.freeTextAllowed,
      additionalKey: extendLocation.matchedPath,
    })

  const expenseCategoryCellData =
    props.params.row.expenseCategoryCellData[`ec:${props.category.id}`]
  const shouldStartWithEmptyValue =
    expenseCategoryCellData?.showMissingWhenEmpty &&
    props.category.freeTextAllowed &&
    !props.params.value

  // override for case when exp cat is required & freeText & no labels & currently empty,
  // to prevent "Missing" being prefilled as formattedValue
  const modifiedParams = shouldStartWithEmptyValue
    ? {
        ...props.params,
        formattedValue: '',
      }
    : props.params

  return props.category.freeTextAllowed && !props.category.hasLabels ? (
    <TextFieldCell
      params={modifiedParams}
      editMode={props.editMode}
      loadingState={props.loading ? 'pending' : 'idle'}
      disabled={props.disabled || props.loading}
    />
  ) : (
    <SingleDropdownCell
      optional
      optionGroups={optionGroups}
      params={modifiedParams}
      editMode={props.editMode}
      loadingState={props.loading ? 'pending' : 'idle'}
      disabled={props.disabled}
      searchable
      infiniteScrollProps={infiniteScrollProps}
      freeTextProps={{
        enabled: props.category.freeTextAllowed,
        existingOptionHeader: translate('Existing Labels'),
        freeTextOptionHeader: translate('New Label'),
      }}
      emptyMessage={{
        title: translate('No active labels'),
        subtitle: translate(
          'Looks like there are no active labels to choose from',
        ),
      }}
    />
  )
}

export default TransactionExpenseCategoryDropdownCell
